import React, { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  LinearProgress,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Chip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import UploadIcon from "@mui/icons-material/Upload";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";
import { useUploadImage, useUpdateImage } from "../../hooks/useImages";
import { useAlbums, useUpdateAlbum, useCreateAlbum } from "../../hooks/useAlbums";
import type { AlbumCreate } from "../../hooks/useAlbums";
import { getAlbumApiAdminAlbumsAlbumIdGet, getAlbumImagesApiAdminAlbumsAlbumIdImagesGet } from "../../api/generated";
import {
  useCategories,
  useSubcategories,
  useCreateCategory,
} from "../../hooks/useCategories";
import { GalleryForm } from "./GalleryForm";
import {
  cancelButtonStyle,
  iconButtonUnderlineStyle,
  dangerIconButtonUnderlineStyle,
  textButtonUnderlineStyle,
} from "../../styles/buttonStyles";

interface UploadedFile {
  id: string;
  file: File;
  preview: string;
  progress: number;
  status: "pending" | "uploading" | "complete" | "error";
  publicId?: string;
  url?: string;
  error?: string;
  title?: string;
  description?: string;
  altText?: string;
  categoryId?: string;
  subcategoryId?: string;
  is_public?: boolean;
}

interface ImageUploadModalProps {
  open: boolean;
  onClose: () => void;
}

export const ImageUploadModal: React.FC<ImageUploadModalProps> = ({
  open,
  onClose,
}) => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [selectedGallery, setSelectedGallery] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [expandedFiles, setExpandedFiles] = useState<Set<string>>(new Set());
  const [isPublic, setIsPublic] = useState(false);
  const [uploadedImageIds, setUploadedImageIds] = useState<string[]>([]); // Track uploaded image IDs for batch gallery update
  const uploadImageMutation = useUploadImage();
  const updateImageMutation = useUpdateImage({ showSuccessMessage: false }); // Suppress success message when called from upload flow
  const updateAlbumMutation = useUpdateAlbum(); // For adding images to galleries
  const createAlbumMutation = useCreateAlbum();
  const createCategoryMutation = useCreateCategory();
  const { data: albumsData, isLoading: galleriesLoading } = useAlbums({ page_size: 100 });
  const galleries = albumsData?.items || [];
  const [galleryFormOpen, setGalleryFormOpen] = useState(false);
  const { data: categories, isLoading: categoriesLoading } = useCategories();
  const { data: subcategories, isLoading: subcategoriesLoading } =
    useSubcategories();
  const { enqueueSnackbar } = useSnackbar();

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're actually leaving the drop zone
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragging(false);
    }
  }, []);

  const processFiles = useCallback((fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    
    // Convert FileList to array immediately to preserve all files
    const fileArray: File[] = [];
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList.item(i);
      if (file) {
        fileArray.push(file);
      }
    }
    
    console.log(`Processing ${fileArray.length} files from FileList`); // Debug log
    
    const newFiles: UploadedFile[] = fileArray
      .filter((file) => file.type.startsWith("image/"))
      .map((file) => {
        const id = Math.random().toString(36).substr(2, 9);
        const preview = URL.createObjectURL(file);
        return {
          id,
          file,
          preview,
          progress: 0,
          status: "pending" as const,
          is_public: isPublic,
        };
      });

    console.log(`Adding ${newFiles.length} image files to state`); // Debug log
    
    if (newFiles.length > 0) {
      setFiles((prev) => {
        const updated = [...prev, ...newFiles];
        console.log(`Total files in state after update: ${updated.length}`); // Debug log
        return updated;
      });
    }
  }, [isPublic]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    // Access files immediately - FileList is live and may be cleared after event
    const droppedFiles = e.dataTransfer.files;
    console.log(`Dropped event received, FileList length: ${droppedFiles.length}`); // Debug log
    
    if (!droppedFiles || droppedFiles.length === 0) {
      console.log("No files in dataTransfer");
      return;
    }

    // Log each file to verify they're all there
    for (let i = 0; i < droppedFiles.length; i++) {
      const file = droppedFiles.item(i);
      if (file) {
        console.log(`File ${i + 1}/${droppedFiles.length}: ${file.name}, type: ${file.type}, size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      }
    }
    
    // Process files immediately while FileList is still valid
    processFiles(droppedFiles);
  }, [processFiles]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files: selectedFiles } = e.target;
    if (selectedFiles && selectedFiles.length > 0) {
      processFiles(selectedFiles);
    }
  };

  const uploadFile = async (uploadFile: UploadedFile) => {
    try {
      // Generate a unique public ID
      const timestamp = Date.now();
      const publicId = `${selectedGallery || "gallery"}-${timestamp}-${
        uploadFile.id
      }`;

      // Update status to uploading
      setFiles((prev) =>
        prev.map((f) =>
          f.id === uploadFile.id
            ? { ...f, status: "uploading" as const, progress: 0 }
            : f
        )
      );

      // Set default values if not provided
      const title = uploadFile.title || uploadFile.file.name;
      const description = uploadFile.description || "";
      const altText = uploadFile.altText || "";

      // Upload using the mutation hook
      const result = await uploadImageMutation.mutateAsync({
        file: uploadFile.file,
        public_id: publicId,
        folder: `mariah-photography/${selectedGallery || "general"}`,
        title: title,
        description: description,
        alt_text: altText,
      });

      // Handle category assignment - update the image with category information
      let categoryId = uploadFile.categoryId || "";
      const subcategoryId = uploadFile.subcategoryId || "";

      // If no category is selected, assign to "Uncategorized" category
      if (!categoryId) {
        const uncategorizedId = await ensureUncategorizedCategory();
        if (uncategorizedId) {
          categoryId = uncategorizedId;
        }
      }

      // Always update the image with category information
      try {
        await updateImageMutation.mutateAsync({
          imageId: result.data.id,
          data: {
            category_id: categoryId || undefined,
            subcategory_id: subcategoryId || undefined,
          },
        });
      } catch (error) {
        console.error("Failed to update image category:", error);
        enqueueSnackbar(
          `Warning: Image uploaded but category assignment failed for ${uploadFile.file.name}`,
          { variant: "warning" }
        );
      }

      // Set public status if needed (images are private by default, so toggle if public)
      if (uploadFile.is_public) {
        try {
          const { toggleAdminImageVisibilityApiAdminImagesImageIdVisibilityPatch } = await import("../../api/generated");
          await toggleAdminImageVisibilityApiAdminImagesImageIdVisibilityPatch({
            path: { image_id: result.data.id },
          });
        } catch (error) {
          console.error("Failed to set image as public:", error);
          enqueueSnackbar(
            `Warning: Image uploaded but failed to set as public for ${uploadFile.file.name}`,
            { variant: "warning" }
          );
        }
      }

      // Track uploaded image ID for batch gallery update later
      if (selectedGallery && result.data.id) {
        setUploadedImageIds((prev) => [...prev, result.data.id]);
        console.log(`Tracked image ${result.data.id} for gallery ${selectedGallery}`);
      }

      // Update with success
      setFiles((prev) =>
        prev.map((f) =>
          f.id === uploadFile.id
            ? {
                ...f,
                status: "complete" as const,
                progress: 100,
                publicId: result.data.public_id,
                url: result.data.url,
              }
            : f
        )
      );
    } catch (error) {
      // Update with error
      setFiles((prev) =>
        prev.map((f) =>
          f.id === uploadFile.id
            ? {
                ...f,
                status: "error" as const,
                error: error instanceof Error ? error.message : "Upload failed",
              }
            : f
        )
      );
    }
  };

  const handleRemove = (fileId: string) => {
    setFiles((prev) => {
      const file = prev.find((f) => f.id === fileId);
      if (file) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter((f) => f.id !== fileId);
    });
    setExpandedFiles((prev) => {
      const newSet = new Set(prev);
      newSet.delete(fileId);
      return newSet;
    });
  };

  const toggleExpanded = (fileId: string) => {
    setExpandedFiles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }
      return newSet;
    });
  };

  // Helper function to ensure "Uncategorized" category exists
  const ensureUncategorizedCategory = async () => {
    if (!categories?.items) return null;

    // Check if "Uncategorized" category already exists
    const uncategorizedCategory = categories.items.find(
      (cat) => cat.name.toLowerCase() === "uncategorized"
    );

    if (uncategorizedCategory) {
      return uncategorizedCategory.id;
    }

    // Create "Uncategorized" category if it doesn't exist
    try {
      const result = await createCategoryMutation.mutateAsync({
        name: "Uncategorized",
        description: "Default category for images without a specific category",
      });
      return result.id;
    } catch (error) {
      console.error("Failed to create Uncategorized category:", error);
      return null;
    }
  };

  const handleUploadComplete = () => {
    // Start uploading files that haven't been uploaded yet
    const filesToUpload = files.filter((f) => f.status === "pending");

    if (filesToUpload.length === 0) {
      alert("No files to upload!");
      return;
    }

    // Upload files with delay to prevent browser freeze
    filesToUpload.forEach((file, index) => {
      setTimeout(() => {
        uploadFile(file);
      }, index * 1000); // 1 second delay between each upload
    });
  };

  // Batch update gallery with all uploaded images when uploads complete
  useEffect(() => {
    const performBatchGalleryUpdate = async () => {
      // Only proceed if we have a gallery selected and images to add
      if (!selectedGallery || uploadedImageIds.length === 0) {
        return;
      }

      // Check if all uploads are complete (no pending or uploading)
      const hasPendingOrUploading = files.some(
        (f) => f.status === "pending" || f.status === "uploading"
      );

      if (hasPendingOrUploading) {
        return; // Wait for all uploads to finish
      }

      // Check if there are any files (otherwise this is a stale trigger)
      if (files.length === 0) {
        return;
      }

      try {
        console.log(`Batch updating gallery ${selectedGallery} with ${uploadedImageIds.length} images`);

        // Fetch current gallery images ONCE
        const imagesResponse = await getAlbumImagesApiAdminAlbumsAlbumIdImagesGet({
          path: { album_id: selectedGallery },
        });

        let currentImageIds: string[] = [];
        if (imagesResponse.data) {
          currentImageIds = (imagesResponse.data.items || [])
            .map((img) => img.id || img.cloudinary_public_id)
            .filter(Boolean) as string[];
        }

        // Add all new images that aren't already in the gallery
        const newImageIds = uploadedImageIds.filter(
          (id) => !currentImageIds.includes(id)
        );

        if (newImageIds.length > 0) {
          const updatedImageIds = [...currentImageIds, ...newImageIds];

          await updateAlbumMutation.mutateAsync({
            albumId: selectedGallery,
            data: {
              image_ids: updatedImageIds,
            },
          });

          console.log(`Successfully added ${newImageIds.length} images to gallery ${selectedGallery}`);
          enqueueSnackbar(
            `Successfully added ${newImageIds.length} image(s) to gallery`,
            { variant: "success" }
          );
        } else {
          console.log("All images already in gallery, skipping update");
        }

        // Clear the uploaded image IDs after successful update
        setUploadedImageIds([]);
      } catch (error) {
        console.error("Failed to batch update gallery:", error);
        enqueueSnackbar("Failed to add images to gallery. Please try again.", {
          variant: "error",
        });
        // Don't clear uploadedImageIds on error so it can be manually retried
      }
    };

    performBatchGalleryUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, selectedGallery, uploadedImageIds]);

  // Auto-close modal when all files are successfully uploaded
  useEffect(() => {
    // Only auto-close if there are files and all are complete (no pending or uploading)
    if (files.length > 0) {
      const hasPendingOrUploading = files.some(
        (f) => f.status === "pending" || f.status === "uploading"
      );
      const allSuccessful =
        !hasPendingOrUploading && files.every((f) => f.status === "complete");

      // Only auto-close if all files uploaded successfully (no errors)
      if (allSuccessful) {
        // Small delay to show the success state before closing
        const timer = setTimeout(() => {
          // Clean up object URLs
          files.forEach((file) => {
            URL.revokeObjectURL(file.preview);
          });
          setFiles([]);
          setSelectedGallery("");
          setIsDragging(false);
          setExpandedFiles(new Set());
          setUploadedImageIds([]); // Clear uploaded image IDs
          onClose();
        }, 1500); // 1.5 second delay to show success
        return () => clearTimeout(timer);
      }
    }
  }, [files, onClose]);

  const handleClose = () => {
    // Clean up object URLs
    files.forEach((file) => {
      URL.revokeObjectURL(file.preview);
    });
    setFiles([]);
    setSelectedGallery("");
    setIsDragging(false);
    setExpandedFiles(new Set());
    setIsPublic(false);
    setUploadedImageIds([]); // Clear uploaded image IDs
    onClose();
  };

  const getStatusText = (file: UploadedFile) => {
    switch (file.status) {
      case "pending":
        return (
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: "0.75rem", color: "text.secondary" }}>
            Ready
          </Typography>
        );
      case "uploading":
        return (
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: "0.75rem", color: "primary.main" }}>
            Uploading...
          </Typography>
        );
      case "complete":
        return (
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: "0.75rem", color: "success.main" }}>
            Complete
          </Typography>
        );
      case "error":
        return (
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: "0.75rem", color: "error.main" }}>
            Error
          </Typography>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          overflow: "hidden",
          width: { xs: "100%", sm: "800px" },
          maxWidth: { xs: "100%", sm: "800px" },
          margin: { xs: 0, sm: "32px" },
          maxHeight: { xs: "100vh", sm: "90vh" },
          height: { xs: "100vh", sm: "auto" },
          borderRadius: { xs: 0, sm: "4px" },
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 3, sm: 4 },
          pt: { xs: 3, sm: 4 },
          pb: { xs: 2, sm: 2 },
        }}
      >
        Upload Images
      </DialogTitle>
      <DialogContent
        sx={{
          px: 4,
          py: 2,
          pb: 3, // Extra bottom padding to separate from actions
          overflowY: "auto", // Enable scrolling within content
          overflowX: "hidden", // Prevent horizontal scroll
          maxHeight: "calc(100vh - 200px)", // Limit height to viewport
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0, 0, 0, 0.2)",
            borderRadius: "4px",
            "&:hover": {
              background: "rgba(0, 0, 0, 0.3)",
            },
          },
        }}
      >
        {/* Gallery Selection */}
        <FormControl fullWidth sx={{ mb: 3, maxWidth: 400 }} variant="standard">
          <InputLabel>Select Gallery</InputLabel>
          <Select
            value={selectedGallery}
            label="Select Gallery"
            onChange={(e) => {
              const value = e.target.value;
              if (value === "__create_new__") {
                setGalleryFormOpen(true);
              } else {
                setSelectedGallery(value);
              }
            }}
            disabled={galleriesLoading}
            renderValue={(value) => {
              if (value === "__create_new__") return "";
              const gallery = galleries?.find((g) => g.id === value);
              return gallery ? gallery.title : "";
            }}
          >
            {galleriesLoading && (
              <MenuItem disabled>Loading galleries...</MenuItem>
            )}
            <MenuItem value="__create_new__" sx={{ fontStyle: "italic", color: "primary.main" }}>
              + Create New Gallery...
            </MenuItem>
            {galleries?.map((gallery) => (
              <MenuItem key={gallery.id} value={gallery.id}>
                {gallery.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Public Toggle */}
        <Box sx={{ mb: 3, maxWidth: 400 }}>
          <FormControlLabel
            control={
              <Switch
                checked={isPublic}
                onChange={(e) => {
                  const newValue = e.target.checked;
                  setIsPublic(newValue);
                  // Update all pending files with the new public status
                  setFiles((prev) =>
                    prev.map((f) =>
                      f.status === "pending"
                        ? { ...f, is_public: newValue }
                        : f
                    )
                  );
                }}
                disabled={files.some((f) => f.status === "uploading" || f.status === "complete")}
              />
            }
            label={
              <Box
                sx={{
                  fontSize: { xs: "16px", sm: "14px" },
                  fontWeight: 500,
                }}
              >
                Make images public
              </Box>
            }
            sx={{
              margin: 0,
              alignItems: "center",
              "& .MuiFormControlLabel-label": {
                marginLeft: { xs: 2, sm: 1.5 },
              },
            }}
          />
        </Box>

        {/* Compact Drop Zone */}
        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            border: "2px dashed",
            borderColor: isDragging ? "primary.main" : "divider",
            backgroundColor: isDragging ? "rgba(0,0,0,0.02)" : "transparent",
            borderRadius: 2,
            p: 3,
            textAlign: "center",
            cursor: "pointer",
            transition: "all 0.2s ease",
            mb: 3,
            minHeight: 120,
            maxWidth: 400,
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CloudUploadIcon
            sx={{
              fontSize: 32,
              color: isDragging ? "primary.main" : "text.secondary",
              mb: 1,
            }}
          />
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              mb: 0.5,
            }}
          >
            Drag and drop images here
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 2,
            }}
          >
            or click to browse (multiple files supported)
          </Typography>
          <Button
            variant="text"
            component="label"
            disableRipple
            size="small"
            sx={textButtonUnderlineStyle}
          >
            Select Files
            <input
              type="file"
              style={{ display: "none" }}
              multiple
              accept="image/*"
              onChange={handleFileSelect}
            />
          </Button>
        </Box>

        {/* Uploaded Files - Compact List */}
        {files.length > 0 && (
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: "text.primary",
              }}
            >
              Uploaded Files ({files.length})
            </Typography>

            <Box sx={{ mb: 3 }}>
              {files.map((file) => (
                <Box key={file.id} sx={{ mb: 1 }}>
                  {/* File Row */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 2,
                      backgroundColor: "background.paper",
                      position: "relative",
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    {/* Thumbnail */}
                    <Box
                      component="img"
                      src={file.preview}
                      alt={file.file.name}
                      sx={{
                        width: 60,
                        height: 60,
                        objectFit: "cover",
                        borderRadius: 1,
                        flexShrink: 0,
                      }}
                    />

                    {/* File Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            flex: 1,
                          }}
                        >
                          {file.title || file.file.name}
                        </Typography>
                        {!file.title && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.7rem",
                              fontStyle: "italic",
                            }}
                          >
                            (default)
                          </Typography>
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: "block",
                          mb: 1,
                        }}
                      >
                        {(file.file.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>

                      {/* Status */}
                      {getStatusText(file)}
                    </Box>

                    {/* Progress Bar */}
                    {file.status === "uploading" && (
                      <Box sx={{ width: 100, mr: 2 }}>
                        <LinearProgress
                          variant="determinate"
                          value={file.progress}
                          sx={{ height: 4 }}
                        />
                      </Box>
                    )}

                    {/* Error Message */}
                    {file.status === "error" && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "error.main",
                          mr: 2,
                        }}
                      >
                        {file.error || "Upload failed"}
                      </Typography>
                    )}

                    {/* Edit Button */}
                    <IconButton
                      size="small"
                      onClick={() => toggleExpanded(file.id)}
                      sx={iconButtonUnderlineStyle}
                    >
                      {expandedFiles.has(file.id) ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <EditIcon fontSize="small" />
                      )}
                    </IconButton>

                    {/* Remove Button */}
                    <IconButton
                      size="small"
                      onClick={() => handleRemove(file.id)}
                      sx={dangerIconButtonUnderlineStyle}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* Expanded Form */}
                  {expandedFiles.has(file.id) && (
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: "grey.50",
                        mt: 1,
                        borderRadius: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        {/* Title */}
                        <TextField
                          fullWidth
                          size="small"
                          label="Title"
                          value={file.title || ""}
                          onChange={(e) =>
                            setFiles((prev) =>
                              prev.map((f) =>
                                f.id === file.id
                                  ? { ...f, title: e.target.value }
                                  : f
                              )
                            )
                          }
                          placeholder={`Default: ${file.file.name}`}
                        />

                        {/* Description */}
                        <TextField
                          fullWidth
                          size="small"
                          label="Description"
                          value={file.description || ""}
                          onChange={(e) =>
                            setFiles((prev) =>
                              prev.map((f) =>
                                f.id === file.id
                                  ? { ...f, description: e.target.value }
                                  : f
                              )
                            )
                          }
                          placeholder="Enter description (optional)"
                        />

                        {/* Alt Text */}
                        <TextField
                          fullWidth
                          size="small"
                          label="Alt Text"
                          value={file.altText || ""}
                          onChange={(e) =>
                            setFiles((prev) =>
                              prev.map((f) =>
                                f.id === file.id
                                  ? { ...f, altText: e.target.value }
                                  : f
                              )
                            )
                          }
                          placeholder="Describe the image for accessibility"
                        />

                        {/* Category and Subcategory */}
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <FormControl
                            fullWidth
                            size="small"
                            variant="standard"
                          >
                            <InputLabel>Category</InputLabel>
                            <Select
                              value={file.categoryId || ""}
                              label="Category"
                              onChange={(e) =>
                                setFiles((prev) =>
                                  prev.map((f) =>
                                    f.id === file.id
                                      ? {
                                          ...f,
                                          categoryId: e.target.value,
                                          subcategoryId: "",
                                        }
                                      : f
                                  )
                                )
                              }
                              disabled={categoriesLoading}
                            >
                              <MenuItem value="">
                                <em>Uncategorized (default)</em>
                              </MenuItem>
                              {categories?.items.map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                  {category.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          <FormControl
                            fullWidth
                            size="small"
                            variant="standard"
                          >
                            <InputLabel>Subcategory</InputLabel>
                            <Select
                              value={file.subcategoryId || ""}
                              label="Subcategory"
                              onChange={(e) =>
                                setFiles((prev) =>
                                  prev.map((f) =>
                                    f.id === file.id
                                      ? { ...f, subcategoryId: e.target.value }
                                      : f
                                  )
                                )
                              }
                              disabled={
                                subcategoriesLoading || !file.categoryId
                              }
                            >
                              <MenuItem value="">
                                <em>No Subcategory</em>
                              </MenuItem>
                              {subcategories?.items
                                .filter(
                                  (sub) => sub.category_id === file.categoryId
                                )
                                .map((subcategory) => (
                                  <MenuItem
                                    key={subcategory.id}
                                    value={subcategory.id}
                                  >
                                    {subcategory.name}
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ gap: 3, px: 4, py: 3, pt: 3, pb: 3, mt: 1 }}>
        <Button
          onClick={handleClose}
          disabled={files.some((f) => f.status === "uploading")}
          disableRipple
          startIcon={<CloseIcon />}
          sx={{
            ...cancelButtonStyle,
            "&:disabled": {
              color: "text.disabled",
              textDecoration: "none",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleUploadComplete}
          disabled={
            files.length === 0 || files.some((f) => f.status === "uploading")
          }
          disableRipple
          startIcon={<UploadIcon />}
          sx={{
            ...cancelButtonStyle,
            "&:disabled": {
              color: "text.disabled",
              textDecoration: "none",
            },
          }}
        >
          {files.some((f) => f.status === "uploading")
            ? "Uploading..."
            : "Upload"}
        </Button>
      </DialogActions>

      {/* Gallery Form Modal */}
      <GalleryForm
        open={galleryFormOpen}
        onClose={() => setGalleryFormOpen(false)}
        onSubmit={async (data) => {
          const result = await createAlbumMutation.mutateAsync(data as AlbumCreate);
          setSelectedGallery(result.id);
          setGalleryFormOpen(false);
        }}
        isLoading={createAlbumMutation.isPending}
        error={createAlbumMutation.error?.message || null}
      />
    </Dialog>
  );
};
